import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AiRmrOverviewMetric, AiRmrSummary } from "./aiRmrSummaryTypes";
import AiSummaryBiologicalAgeCard from "./AiSummaryBiologicalAgeCard";
import AiSummaryRecommendationsView, {
  hasAiSummaryRecommendations,
} from "./AiSummaryRecommendationsView";
import classes from "./AiRmrSummary.module.css";

type Props = {
  summary: AiRmrSummary;
};

type MetricTone = "danger" | "warning" | "neutral" | "success";

const METRIC_GROUP_LABELS: Record<string, string> = {
  "3_RMR": "Mitochondria",
  "4_RMR": "Heart",
  "8_RMR": "Mitochondria",
  "11_RMR": "Metabolism",
  "13_RMR": "Respiratory function",
  "15_RMR": "Respiratory function",
  "19_RMR": "Posture",
  "25_RMR": "Nervous system",
  "26_RMR": "Lung",
};

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const formatDate = (value: string): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getMetricTone = (scoreCategory?: number): MetricTone => {
  if (scoreCategory === 1) {
    return "danger";
  }
  if (scoreCategory === 2) {
    return "warning";
  }
  if (scoreCategory === 4 || scoreCategory === 5) {
    return "success";
  }
  return "neutral";
};

const metricToneClass: Record<MetricTone, string> = {
  danger: classes.metricToneDanger,
  warning: classes.metricToneWarning,
  neutral: classes.metricToneNeutral,
  success: classes.metricToneSuccess,
};

const OverviewMetricCard = ({ metric }: { metric: AiRmrOverviewMetric }) => {
  const tone = getMetricTone(metric.scoreCategory);
  const progress = clampPercent(metric.value);

  return (
    <article className={`${classes.metricCard} ${metricToneClass[tone]}`}>
      <div
        className={classes.metricProgress}
        role="progressbar"
        aria-label={`${metric.label} score`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <circle
            className={classes.metricProgressTrack}
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
          />
          <circle
            className={classes.metricProgressValue}
            cx="22"
            cy="22"
            r="18"
            pathLength="100"
            strokeDasharray={`${progress} ${100 - progress}`}
          />
        </svg>
        <span>
          {formatNumber(metric.value)}
          {metric.unit || "%"}
        </span>
      </div>
      <div className={classes.metricContent}>
        <p className={classes.metricGroup}>
          {METRIC_GROUP_LABELS[metric.id] || "Health metric"}
        </p>
        <h4>{metric.label}</h4>
        {metric.scoreCategoryLabel && (
          <span className={classes.metricCategory}>
            {metric.scoreCategoryLabel}
          </span>
        )}
      </div>
    </article>
  );
};

const RmrCard = ({ summary }: Props) => {
  if (!summary.hero.rmr) {
    return null;
  }

  return (
    <article className={classes.heroCard}>
      <p className={classes.cardEyebrow}>Resting metabolic rate (RMR)</p>
      <div className={classes.rmrValue}>
        <strong>{formatNumber(summary.hero.rmr.value)}</strong>
        {summary.hero.rmr.unit && <span>{summary.hero.rmr.unit}</span>}
      </div>
      {summary.rmrDescription && <p>{summary.rmrDescription}</p>}
    </article>
  );
};

const FuelCard = ({ summary }: Props) => {
  const fatPercent = summary.hero.fatPercent;
  const carbPercent = summary.hero.carbPercent;
  if (fatPercent === undefined && carbPercent === undefined) {
    return null;
  }

  const fat = clampPercent(fatPercent ?? 0);
  const carbs = clampPercent(carbPercent ?? 0);
  const total = fat + carbs || 1;

  return (
    <article className={classes.heroCard}>
      <p className={classes.cardEyebrow}>Fuel sources at rest</p>
      <div className={classes.fuelBar} aria-label="Fuel source distribution">
        <span
          className={classes.fuelFat}
          style={{ width: `${(fat / total) * 100}%` }}
        />
        <span
          className={classes.fuelCarbs}
          style={{ width: `${(carbs / total) * 100}%` }}
        />
      </div>
      <div className={classes.legendRow}>
        {fatPercent !== undefined && (
          <span>
            <i className={classes.fatDot} /> Fats {formatNumber(fatPercent)}%
          </span>
        )}
        {carbPercent !== undefined && (
          <span>
            <i className={classes.carbDot} /> Carbs {formatNumber(carbPercent)}%
          </span>
        )}
      </div>
      {summary.fuelDescription && <p>{summary.fuelDescription}</p>}
    </article>
  );
};

const AiRmrSummaryView = ({ summary }: Props) => {
  const hasIdentity = Boolean(
    summary.patient.companyName ||
      summary.legend ||
      summary.patient.name ||
      summary.patient.testDate ||
      summary.reportType
  );
  const hasHero = Boolean(
    summary.hero.rmr ||
      summary.hero.fatPercent !== undefined ||
      summary.hero.carbPercent !== undefined ||
      summary.patient.biologicalAge !== undefined ||
      summary.patient.chronologicalAge !== undefined
  );
  const hasRecommendations = hasAiSummaryRecommendations({
    trainingPlan: summary.trainingPlan,
    nutrition: summary.nutrition,
    recommendations: summary.recommendations,
  });
  const hasContent = Boolean(
    hasIdentity ||
      hasHero ||
      summary.overviewMetrics.length > 0 ||
      summary.measuredValues.length > 0 ||
      hasRecommendations ||
      summary.disclaimer
  );

  if (!hasContent) {
    return (
      <section className={classes.emptyState}>
        <h3>No RMR summary content was returned</h3>
        <p>The AI response did not include any displayable RMR fields.</p>
      </section>
    );
  }

  return (
    <div className={classes.report}>
      {hasIdentity && (
        <header className={classes.reportHeader}>
          <div>
            {summary.patient.companyName && (
              <h3>{summary.patient.companyName}</h3>
            )}
            {summary.legend && <p>{summary.legend}</p>}
          </div>
          <div className={classes.patientIdentity}>
            {summary.patient.name && <h3>{summary.patient.name}</h3>}
            {summary.patient.testDate && (
              <p>Test date: {formatDate(summary.patient.testDate)}</p>
            )}
            {summary.reportType && <span>{summary.reportType}</span>}
          </div>
        </header>
      )}

      {hasHero && (
        <section className={classes.heroGrid}>
          <AiSummaryBiologicalAgeCard
            biologicalAge={summary.patient.biologicalAge}
            chronologicalAge={summary.patient.chronologicalAge}
            description={summary.bioAgeDescription}
          />
          <RmrCard summary={summary} />
          <FuelCard summary={summary} />
        </section>
      )}

      {summary.overviewMetrics.length > 0 && (
        <section className={classes.reportSection}>
          <h3 className={classes.sectionTitle}>Overview</h3>
          <div className={classes.overviewGrid}>
            {summary.overviewMetrics.map((metric) => (
              <OverviewMetricCard key={metric.id} metric={metric} />
            ))}
          </div>
        </section>
      )}

      {summary.measuredValues.length > 0 && (
        <section className={classes.measuredSection}>
          <h3 className={classes.measuredTitle}>Measured values</h3>
          <div className={classes.measuredGrid}>
            {summary.measuredValues.map((measurement) => (
              <div key={measurement.id} className={classes.measuredValue}>
                <span>{measurement.label}</span>
                <strong>{formatNumber(measurement.value)}</strong>
                {measurement.unit && <small>{measurement.unit}</small>}
              </div>
            ))}
          </div>
        </section>
      )}

      {hasRecommendations && (
        <AiSummaryRecommendationsView
          trainingPlan={summary.trainingPlan}
          nutrition={summary.nutrition}
          recommendations={summary.recommendations}
        />
      )}

      {summary.disclaimer && (
        <section className={classes.disclaimer}>
          <InfoOutlinedIcon aria-hidden="true" />
          <div>
            <strong>Disclaimer</strong>
            <p>{summary.disclaimer}</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default AiRmrSummaryView;
